import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, FileSignature, Download, Copy, Check } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { PageHeader } from '../../components/shared/PageHeader';
import { LoadingState } from '../../components/shared/LoadingState';
import { ErrorState } from '../../components/shared/ErrorState';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/Card';
import { useSignatureEnvelopes } from '../../core/hooks/useSignatureEnvelopes';
import { useEnvelopes } from '../../core/hooks/useEnvelopes';
import { useAuth } from '../../core/contexts/AuthContext';
import { SignatureEnvelopeStatus } from '../../core/models/SignatureEnvelope';
import { AuditTimeline } from './components/AuditTimeline';
import { logError } from '../../lib/utils';

export const EnvelopeDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { sendToClient, getFinalPdf, getEnvelope, loading } = useSignatureEnvelopes();
  const { envelopes } = useEnvelopes(null);
  const [envelope, setEnvelope] = useState<any>(null);
  const [envelopeLoading, setEnvelopeLoading] = useState(true);
  const [signingLink, setSigningLink] = useState<string>('');
  const [linkCopied, setLinkCopied] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!id) return;

    const fetchEnvelope = async () => {
      setEnvelopeLoading(true);
      try {
        const found = envelopes.find(e => e.envelopeId === id);
        if (found) {
          setEnvelope(found);
        } else {
          const fetched = await getEnvelope(id);
          setEnvelope(fetched);
        }
      } catch (err) {
        logError('signatures', err, { source: 'EnvelopeDetailPage.fetchEnvelope', envelopeId: id });
      } finally {
        setEnvelopeLoading(false);
      }
    };

    fetchEnvelope();
  }, [id, envelopes, getEnvelope]);

  const handleSendToClient = async () => {
    if (!id) return;

    setSending(true);
    try {
      const result = await sendToClient(id, true);
      setSigningLink(result.signingLink);
      setEnvelope((prev: any) => ({
        ...prev,
        status: SignatureEnvelopeStatus.SENT,
      }));
    } catch (err: any) {
      logError('signatures', err, { source: 'EnvelopeDetailPage.handleSendToClient', envelopeId: id });
      alert(err.message || 'Failed to send envelope to client');
    } finally {
      setSending(false);
    }
  };

  const handleCopyLink = () => {
    if (signingLink || envelope?.clientToken) {
      const link = signingLink || `${window.location.origin}/sign/${envelope.clientToken}`;
      navigator.clipboard.writeText(link);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }
  };

  const handleDownloadFinalPdf = async () => {
    if (!id) return;

    try {
      const { downloadUrl } = await getFinalPdf(id);
      window.open(downloadUrl, '_blank');
    } catch (err: any) {
      logError('signatures', err, { source: 'EnvelopeDetailPage.handleDownloadFinalPdf', envelopeId: id });
      alert(err.message || 'Failed to download final PDF');
    }
  };

  const getStatusColor = (status: SignatureEnvelopeStatus) => {
    switch (status) {
      case SignatureEnvelopeStatus.COMPLETED:
        return 'success';
      case SignatureEnvelopeStatus.CLIENT_SIGNED:
        return 'warning';
      case SignatureEnvelopeStatus.SENT:
        return 'secondary';
      case SignatureEnvelopeStatus.DRAFT:
        return 'default';
      default:
        return 'default';
    }
  };

  if (envelopeLoading || loading) {
    return <LoadingState message="Loading envelope..." />;
  }

  if (!envelope) {
    return <ErrorState message="Envelope not found" />;
  }

  const canSend = envelope.status === SignatureEnvelopeStatus.DRAFT;
  const canSign = envelope.status === SignatureEnvelopeStatus.CLIENT_SIGNED &&
    envelope.companySigner.userId === currentUser?.uid;
  const isCompleted = envelope.status === SignatureEnvelopeStatus.COMPLETED;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Signature Envelope"
        description={`Envelope ID: ${envelope.envelopeId.substring(0, 8)}...`}
        icon={FileSignature}
        actions={
          <Button variant="outline" onClick={() => navigate('/envelopes')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Status Card */}
        <Card>
          <CardHeader>
            <CardTitle>Status</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Badge variant={getStatusColor(envelope.status)} className="text-sm">
                {envelope.status.replace('_', ' ')}
              </Badge>
            </div>
            <div className="text-sm space-y-2">
              <div>
                <span className="font-medium">Source:</span>{' '}
                <Badge variant="outline">
                  {envelope.sourceType === 'pdf_upload' ? 'PDF Upload' : 'Document Template'}
                </Badge>
              </div>
              {envelope.documentId && (
                <div>
                  <span className="font-medium">Document ID:</span>{' '}
                  <button
                    onClick={() => navigate(`/documents/${envelope.documentId}`)}
                    className="text-blue-600 hover:underline"
                  >
                    {envelope.documentId.substring(0, 8)}...
                  </button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Signers Card */}
        <Card>
          <CardHeader>
            <CardTitle>Signers</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="text-sm font-medium">Project</div>
              <div className="text-sm text-gray-600">{envelope.clientSigner.name}</div>
              <div className="text-sm text-gray-500">{envelope.clientSigner.email}</div>
              {envelope.clientSigner.signedAt && (
                <div className="text-xs text-gray-400 mt-1">
                  Signed: {new Date(envelope.clientSigner.signedAt).toLocaleString()}
                </div>
              )}
            </div>
            <div>
              <div className="text-sm font-medium">Company</div>
              <div className="text-sm text-gray-600">{envelope.companySigner.name}</div>
              <div className="text-sm text-gray-500">{envelope.companySigner.email}</div>
              {envelope.companySigner.signedAt && (
                <div className="text-xs text-gray-400 mt-1">
                  Signed: {new Date(envelope.companySigner.signedAt).toLocaleString()}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Actions Card */}
      <Card>
        <CardHeader>
          <CardTitle>Actions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {canSend && (
            <div className="flex gap-2">
              <Button onClick={handleSendToClient} disabled={sending}>
                <Send className="mr-2 h-4 w-4" />
                {sending ? 'Sending...' : 'Send to Project'}
              </Button>
            </div>
          )}

          {(envelope.status === SignatureEnvelopeStatus.SENT || envelope.status === SignatureEnvelopeStatus.CLIENT_SIGNED) && (
            <div className="flex gap-2 items-center">
              <Button variant="outline" onClick={handleCopyLink}>
                {linkCopied ? (
                  <>
                    <Check className="mr-2 h-4 w-4" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="mr-2 h-4 w-4" />
                    Copy Signing Link
                  </>
                )}
              </Button>
              {signingLink && (
                <div className="text-sm text-gray-600 font-mono text-xs break-all">
                  {signingLink}
                </div>
              )}
            </div>
          )}

          {canSign && (
            <Button onClick={() => navigate(`/envelopes/${id}/sign`)}>
              <FileSignature className="mr-2 h-4 w-4" />
              Sign as Company
            </Button>
          )}

          {isCompleted && (
            <Button onClick={handleDownloadFinalPdf}>
              <Download className="mr-2 h-4 w-4" />
              Download Final PDF
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Audit Timeline */}
      {envelope.auditLog && envelope.auditLog.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Audit Timeline</CardTitle>
            <CardDescription>History of envelope actions</CardDescription>
          </CardHeader>
          <CardContent>
            <AuditTimeline auditLog={envelope.auditLog} />
          </CardContent>
        </Card>
      )}
    </div>
  );
};
